import {
  applyOps,
  ProjectError,
  projectHash,
  type Project,
  type ProjectOp,
  type ProjectState,
  type VideoSummary,
} from '@live-stream/shared'
import type { Videos } from '../api/videos.js'
import { ApiError } from '../http.js'
import { patch, readonly, writable, type ReadableStore } from '../store.js'

/**
 * The editor's side of a video's project (E1). A change shows at once:
 * it is checked and applied here with the same `applyOps` the server
 * runs, and queued. The queue is sent in order, each batch for the
 * revision the server last answered with, so the project on screen is
 * always the server's plus what is on its way.
 *
 * When the server has moved on (another tab, say), the editor reloads
 * and says so rather than overwrite what it did not see. A batch the
 * server refuses is dropped with the ones queued after it, which were
 * made on top of it. Undo and redo are the server's, so they survive a
 * reload; they wait for the queue to empty first.
 *
 * Every change is kept as it is made, but the video people watch changes
 * only when the project is rendered (`render()`), or rendered into a new
 * video (`copy()`). `rendered` says whether the video shows the project
 * on screen.
 */

export interface ProjectEditorState {
  videoId: string | null
  loading: boolean
  /** What the editor shows: the saved project, with the changes on their way applied. */
  project: Project | null
  /** The saved revision. */
  rev: number
  canUndo: boolean
  canRedo: boolean
  undoLabel: string | null
  redoLabel: string | null
  /** Batches made but not saved yet. */
  pending: number
  /** The video people watch shows this project: nothing to render. */
  rendered: boolean
  /** Asking for a render, or a copy. */
  rendering: boolean
  /** Something could not be done or saved, in words for the person. */
  error: string | null
}

type ProjectApi = Pick<
  Videos,
  'project' | 'projectSteps' | 'projectUndo' | 'projectRedo' | 'projectRender' | 'projectCopy'
>

interface Batch {
  ops: ProjectOp[]
  label: string
}

const initial = (): ProjectEditorState => ({
  videoId: null,
  loading: false,
  project: null,
  rev: 0,
  canUndo: false,
  canRedo: false,
  undoLabel: null,
  redoLabel: null,
  pending: 0,
  rendered: true,
  rendering: false,
  error: null,
})

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback

export class ProjectEditor {
  private readonly store = writable<ProjectEditorState>(initial())
  readonly state: ReadableStore<ProjectEditorState> = readonly(this.store)

  /** The server's last answer. */
  private saved: ProjectState | null = null
  private queue: Batch[] = []
  private sending: Promise<void> | null = null

  constructor(private readonly api: ProjectApi) {}

  private get videoId(): string {
    const id = this.store.get().videoId
    if (!id) throw new Error('ProjectEditor: open(videoId) first.')
    return id
  }

  /** The saved project with the queue applied: what the person sees. */
  private show(extra: Partial<ProjectEditorState> = {}): void {
    const saved = this.saved
    if (!saved) return
    let project = saved.project
    for (const b of this.queue) project = applyOps(project, b.ops)
    patch(this.store, {
      project,
      rev: saved.rev,
      // While changes are on their way, undo waits; it still shows as possible.
      canUndo: saved.canUndo || this.queue.length > 0,
      canRedo: saved.canRedo && this.queue.length === 0,
      undoLabel: this.queue.length ? this.queue[this.queue.length - 1]!.label : saved.undoLabel,
      redoLabel: this.queue.length ? null : saved.redoLabel,
      pending: this.queue.length,
      rendered: saved.renderedHash !== null && projectHash(project) === saved.renderedHash,
      ...extra,
    })
  }

  /** Load a video's project (made from its cut list the first time). */
  async open(videoId: string): Promise<boolean> {
    this.saved = null
    this.queue = []
    this.store.set({ ...initial(), videoId, loading: true })
    try {
      this.saved = await this.api.project(videoId)
      this.show({ loading: false })
      return true
    } catch (err) {
      patch(this.store, {
        loading: false,
        error: messageOf(err, 'The project could not be opened.'),
      })
      return false
    }
  }

  /**
   * Make a change: it shows at once and is saved in the background.
   * False, with the reason in `error`, when it cannot be made.
   */
  apply(ops: ProjectOp[], label = ''): boolean {
    const project = this.store.get().project
    if (!project || ops.length === 0) return false
    try {
      applyOps(project, ops)
    } catch (err) {
      if (err instanceof ProjectError) {
        patch(this.store, { error: err.message })
        return false
      }
      throw err
    }
    this.queue.push({ ops, label })
    this.show({ error: null })
    void this.flush()
    return true
  }

  /** Send the queue, one batch at a time, in order. Resolves when it is empty or stopped. */
  flush(): Promise<void> {
    this.sending ??= this.send().finally(() => {
      this.sending = null
    })
    return this.sending
  }

  private async send(): Promise<void> {
    while (this.queue.length && this.saved) {
      const batch = this.queue[0]!
      try {
        this.saved = await this.api.projectSteps(
          this.videoId,
          this.saved.rev,
          batch.ops,
          batch.label,
        )
        this.queue.shift()
        this.show()
      } catch (err) {
        await this.failed(err)
        return
      }
    }
  }

  /** A batch that did not save: reload, drop or keep it, and say why. */
  private async failed(err: unknown): Promise<void> {
    if (err instanceof ApiError && err.code === 'stale') {
      this.queue = []
      await this.reload('The project changed in another window, so it was reloaded.')
      return
    }
    if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
      // Refused: it and everything made on top of it go.
      this.queue = []
      this.show({ error: err.message })
      return
    }
    // The connection: keep the changes, and try again when asked.
    this.show({ error: 'Your last changes are not saved yet. Check the connection and try again.' })
  }

  /** Try the unsaved changes again, after a lost connection. */
  retry(): Promise<void> {
    patch(this.store, { error: null })
    return this.flush()
  }

  private async reload(error: string | null = null): Promise<void> {
    try {
      this.saved = await this.api.project(this.videoId)
      this.show({ error })
    } catch (err) {
      patch(this.store, { error: messageOf(err, 'The project could not be loaded.') })
    }
  }

  /** Undo the last change (the server's history, so it survives a reload). */
  async undo(): Promise<void> {
    await this.history('undo')
  }

  async redo(): Promise<void> {
    await this.history('redo')
  }

  private async history(which: 'undo' | 'redo'): Promise<void> {
    await this.flush()
    if (!this.saved || this.queue.length) return
    if (which === 'undo' ? !this.saved.canUndo : !this.saved.canRedo) return
    try {
      this.saved =
        which === 'undo'
          ? await this.api.projectUndo(this.videoId, this.saved.rev)
          : await this.api.projectRedo(this.videoId, this.saved.rev)
      this.show({ error: null })
    } catch (err) {
      if (err instanceof ApiError && err.code === 'stale') {
        await this.reload('The project changed in another window, so it was reloaded.')
      } else {
        this.show({ error: messageOf(err, `Could not ${which}.`) })
      }
    }
  }

  /**
   * Put the project on the video people watch: every change saved first,
   * then rendered in the background. The video it answers with says how far
   * the render has got; null, with the reason in `error`, when it could not
   * start.
   */
  async render(): Promise<VideoSummary | null> {
    return this.rendering('Could not save the video.', async () => {
      const video = await this.api.projectRender(this.videoId)
      if (this.saved) this.saved = { ...this.saved, renderedHash: projectHash(this.saved.project) }
      return video
    })
  }

  /** The project as a new video of its own; this one stays as it is. Null when it could not start. */
  async copy(title?: string): Promise<VideoSummary | null> {
    return this.rendering('Could not save the new video.', () =>
      this.api.projectCopy(this.videoId, title),
    )
  }

  private async rendering(
    fallback: string,
    run: () => Promise<VideoSummary>,
  ): Promise<VideoSummary | null> {
    patch(this.store, { rendering: true, error: null })
    await this.flush()
    if (this.queue.length) {
      // Not everything is saved: the store already says why.
      patch(this.store, { rendering: false })
      return null
    }
    try {
      const video = await run()
      this.show({ rendering: false })
      return video
    } catch (err) {
      patch(this.store, { rendering: false, error: messageOf(err, fallback) })
      return null
    }
  }

  /** Every change is saved: nothing waiting, nothing being sent. */
  get idle(): boolean {
    return this.queue.length === 0 && this.sending === null
  }

  /** Forget the project: another video, or the editor closed. */
  close(): void {
    this.saved = null
    this.queue = []
    this.store.set(initial())
  }
}
